<template>
    <div class="lm-border pa-2 pt-3 pb-3 v-flex" style="position: relative">
        <p-audit-modification
            class  = "escalator-modification"
            :entry = "entry"
        />

        <div style="display: block; width: calc(100% - 30px)" class="pr-0 ml-3">
            <div class="v-flex">
                <div class="catalogue-id">
                    {{ code }}
                </div>

                <div
                    class  = "catalogue-type ml-2"
                    :class = "typeColor"
                >
                    {{ entry.type ? entry.type.name : '---' }}
                </div>

                <v-spacer />
                <div class="v-flex" v-if="editable && isPlaner">
                    <c-toolbar-button
                        color    = "primary"
                        size     = "20"
                        :icon    = "edit ? 'mdi-content-save' : 'mdi-pencil'"
                        :tooltip = "edit ? $t('audit.escalator.save') : $t('audit.escalator.edit')"
                        @click   = "onEdit"
                    />

                    <div class="ml-2">
                        <c-toolbar-button
                            v-if     = "edit"
                            color    = "primary"
                            size     = "28"
                            icon     = "mdi-close"
                            :tooltip = "$t('audit.escalator.cancel')"
                            @click   = "onCancel"
                        />
                    </div>
                </div>
            </div>

            <v-divider class="mt-2 mb-2" />
            <div v-html="entryTitle"></div>

            <div
                v-if  = "measures.length > 0"
                class = "mt-3 mb-3 lm-border pa-3 pt-2"
            >
                <div class="v-flex mb-3" style="justify-content: center">
                    <div>
                        <div class="value-label primary--text">{{ $t('audit.escalator.risk') }}</div>
                        <div class="catalogue-value lm-border">{{ risk ? risk.name : '---' }}</div>
                    </div>
                    <div class="ml-2">
                        <div class="value-label primary--text">{{ $t('audit.escalator.frequence') }}</div>
                        <div class="catalogue-value lm-border">{{ effective.frequence ? effective.frequence.name : '---' }}</div>
                    </div>
                    <div class="ml-2">
                        <div class="value-label primary--text">{{ $t('audit.escalator.weight') }}</div>
                        <div class="catalogue-value lm-border">{{ effective.weight ? effective.weight.name : '---' }}</div>
                    </div>
                </div>

                <div class="primary--text mb-1" style="font-size: 11px">
                    {{ $t('audit.escalator.measures') }}
                </div>

                <div
                    v-for = "measure in measures"
                    :key  = "'m_' + measure.id"
                    class = "v-flex measure-row"
                    :class = "measure.chosen ? '' : 'measure-not-chosen'"
                >
                    <v-icon
                        size   = "18"
                        :color = "measure.chosen ? 'primary' : 'grey'"
                        :title = "measure.chosen ? $t('audit.escalator.chosen') : $t('audit.escalator.notChosen')"
                    >
                        {{ measure.chosen ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline' }}
                    </v-icon>

                    <!-- neutral chip without colour, as in the app (HCIT-1193) -->
                    <div class="measure-kind ml-2 grey lighten-3" v-if="measure.kind">
                        {{ $t('audit.escalator.kind.' + measure.kind) }}
                    </div>

                    <div class="ml-2 measure-text">
                        <div>
                            {{ measure.description }}
                            <span v-if="measure.deleted" class="grey--text">({{ $t('audit.escalator.deleted') }})</span>
                        </div>
                        <div class="measure-values grey--text text--darken-1">
                            {{ $t('audit.escalator.frequence') }}: {{ measure.frequence ? measure.frequence.name : '---' }}
                            · {{ $t('audit.escalator.weight') }}: {{ measure.weight ? measure.weight.name : '---' }}
                        </div>
                    </div>
                </div>

                <v-switch
                    v-if      = "editable"
                    v-model   = "done"
                    :label    = "$t('audit.escalator.done')"
                    :ripple   = "false"
                    :readonly = "!edit"
                    class     = "mt-2"
                    hide-details
                />
            </div>

            <div>
                <f-read-only-field
                    v-if   = "!edit"
                    class  = "mt-2"
                    :label = "$t('audit.escalator.note')"
                    :value = "entry.notice"
                    filled
                    dense
                />
                <v-textarea
                    v-if    = "edit"
                    v-model = "notice"
                    :label  = "$t('audit.escalator.note')"
                    class   = "mt-4"
                    rows    = "2"
                    hide-details
                    no-resize
                />
            </div>
        </div>
    </div>
</template>

<script>
import FReadOnlyField     from "@/modules/core/components/utils/FReadOnlyField.vue";
import CToolbarButton     from "@/modules/core/components/utils/CToolbarButton.vue";
import PAuditModification from "@/modules/audit/components/private/PAuditModification.vue";
import Audit              from "@/modules/audit/service/Audit";
import AuditType          from "@/modules/audit/service/AuditType";
import Core               from "@/modules/core/service/Core";

/**
 * Entry of an escalator GBU (HCIT-917): answer, risk of the answer, frequency and severity of the chosen measures and
 * the measures of the answer with their kind, the chosen ones marked. Answer and measure choice come from the app and
 * are read only; note and "Maßnahme umgesetzt" can be edited (F1). No costs, budget or drive type (Festlegung 10).
 */
export default {
    name: "PAuditEscalatorEntry",

    components: {
        PAuditModification,
        CToolbarButton,
        FReadOnlyField,
    },

    props: {
        auditId: {

        },

        entry: {

        },

        country: {

        },

        /** The catalogue set of the escalator GBU, for the ranks of the frequencies. */
        enums: {

        },

        /** Unit dialog: note and "Maßnahme umgesetzt" can be edited; disposition: read only. */
        editable: {
            required: false,
            type    : Boolean,
            default : function () {
                return false;
            }
        }
    },

    data: () => ({
        edit  : false,
        notice: null,
        done  : false,
    }),

    computed: {
        code () {
            return AuditType.code(this.entry.catalogue);
        },

        entryTitle () {
            if (this.country && this.country === 'AT' && this.entry.catalogue.title_at) {
                return this.entry.catalogue.title_at;
            }

            return this.entry.catalogue.title;
        },

        measures () {
            return AuditType.measures(this.entry, this.country);
        },

        risk () {
            return AuditType.risk(this.entry);
        },

        effective () {
            return AuditType.effective(this.entry, this.enums ? this.enums.frequencies : []);
        },

        typeColor () {
            switch (this.entry.type ? this.entry.type.id : null) {
                case 1: return "success white--text";
                case 2: return "error white--text";
                case 3: return "lm-border black--text";
                case 4: return "warning black--text";
            }

            return null;
        },

        isPlaner () {
            return Core.security.isPlanner() || Core.security.isDispatcher();
        }
    },

    watch: {
        entry: {
            immediate: true,
            handler () {
                this.edit   = false;
                this.notice = this.entry ? this.entry.notice : null;
                this.done   = this.entry ? !!this.entry.done : false;
            }
        }
    },

    methods: {
        onEdit () {
            if (!this.edit) {
                this.notice = this.entry.notice;
                this.done   = !!this.entry.done;
                this.edit   = true;
                return;
            }

            // answer and chosen measures are sent unchanged; audit-svc rewrites the measures from this list
            Audit.save(this.auditId, AuditType.noteUpdate(this.entry, this.notice, this.done)).then(() => {
                this.entry.notice = this.notice;
                this.entry.done   = this.done;
                this.edit         = false;
                Core.message.alert.save(this);
            }).catch(error => {
                // edit mode stays open with the changes
                Core.message.error(this, error);
            });
        },

        onCancel () {
            this.notice = this.entry.notice;
            this.done   = !!this.entry.done;
            this.edit   = false;
        },
    }
}
</script>

<style scoped>

.escalator-modification {
    position: absolute;
    right: -78px;
    transform: rotate(90deg);
    font-size: 11px;
    top: 85px;
    text-align: left;
    width: 175px;
    height: 20px;
    color: grey;
}

.catalogue-id {
    font-size: 16px;
    font-weight: 700;
}

.catalogue-type, .catalogue-value, .measure-kind {
    font-size: 14px;
    padding-left: 8px;
    padding-right: 8px;
    border-radius: 4px;
    padding-top: 1px;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.catalogue-type {
    min-width: 110px;
}

.catalogue-value {
    width: 120px;
}

.value-label {
    font-size: 9px;
    width: 120px;
    text-align: center;
}

.measure-row {
    align-items: flex-start;
    padding-top: 4px;
    padding-bottom: 4px;
}

.measure-kind {
    font-size: 11px;
    min-width: 90px;
}

.measure-text {
    flex: 1;
    min-width: 0;
}

.measure-values {
    font-size: 11px;
}

.measure-not-chosen {
    opacity: 0.6;
}

</style>
